/**
 * Admin Progress Overview
 * 
 * Component for administrators to view and manage student progress
 * across all subjects and grades.
 */

import React, { useState } from 'react';
import { 
  Users, BookOpen, TrendingUp, TrendingDown, AlertTriangle,
  ChevronRight, Search, ArrowLeft, BarChart3, Eye, AlertCircle, 
  CheckCircle2, XCircle
} from 'lucide-react';
import { 
  useAdminProgressOverview, 
  useSubjectStudentsProgress, 
  useStudentProgressDetail,
  useStudentsBehind 
} from '../../hooks/useProgress';
import {
  ScheduleStatus, STATUS_STYLES, formatPercent, getStatusLabel,
  SubjectSummary, StudentProgressForSubject, StudentProgressSummary,
  ProgressOverview
} from '../../types/progress';
import { useThemeStore } from '../../stores/themeStore';
import { getProgressBarColor } from '../../services/progressService';

// ============= SUB-COMPONENTS =============

interface StatusBadgeProps {
  status: ScheduleStatus;
  size?: 'sm' | 'md';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
  const style = STATUS_STYLES[status];
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${style.bgColor} ${style.textColor} ${sizeClasses}`}>
      <span>{style.icon}</span>
      <span>{getStatusLabel(status)}</span>
    </span>
  );
};

interface ProgressBarProps {
  percent: number;
  height?: 'sm' | 'md';
}

const ProgressBar: React.FC<ProgressBarProps> = ({ percent, height = 'md' }) => {
  const heightClasses = { sm: 'h-1.5', md: 'h-2' };
  const color = getProgressBarColor(percent);
  
  return (
    <div className={`w-full bg-slate-700/50 rounded-full overflow-hidden ${heightClasses[height]}`}>
      <div 
        className={`${color} h-full rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  trend?: 'up' | 'down' | 'neutral';
  isDark: boolean;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, trend, isDark, color = 'cyan' }) => {
  const colorClasses: Record<string, string> = {
    cyan: isDark ? 'from-cyan-500/10 to-blue-500/10 border-cyan-500/20' : 'from-cyan-50 to-blue-50 border-cyan-200',
    red: isDark ? 'from-red-500/10 to-orange-500/10 border-red-500/20' : 'from-red-50 to-orange-50 border-red-200',
    green: isDark ? 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20' : 'from-emerald-50 to-teal-50 border-emerald-200',
    purple: isDark ? 'from-purple-500/10 to-violet-500/10 border-purple-500/20' : 'from-purple-50 to-violet-50 border-purple-200',
  };
  
  return (
    <div className={`p-4 rounded-xl border bg-gradient-to-br ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          isDark ? 'bg-white/10' : 'bg-white/50'
        }`}>
          {icon}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {value}
            </span>
            {trend === 'up' && <TrendingUp className="w-4 h-4 text-emerald-400" />}
            {trend === 'down' && <TrendingDown className="w-4 h-4 text-red-400" />}
          </div>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
        </div>
      </div>
    </div>
  );
};

interface SubjectCardProps {
  subject: SubjectSummary;
  onSelect: () => void;
  isDark: boolean;
}

const SubjectCard: React.FC<SubjectCardProps> = ({ subject, onSelect, isDark }) => {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-4 rounded-xl border transition-all group ${
        isDark 
          ? 'bg-white/5 border-white/10 hover:bg-white/10' 
          : 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          <div>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {subject.subject_name}
            </span>
            {subject.grade && (
              <span className={`ml-2 text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                ({subject.grade})
              </span>
            )}
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${
          isDark ? 'text-slate-500' : 'text-slate-400'
        }`} />
      </div>
      
      <div className="flex items-center gap-2 mb-2">
        <ProgressBar percent={subject.average_completion} height="sm" />
        <span className={`text-sm font-medium min-w-[3rem] text-right ${
          isDark ? 'text-slate-300' : 'text-slate-600'
        }`}>
          {formatPercent(subject.average_completion)}
        </span>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
          {subject.total_students} students
        </span>
        <div className="flex items-center gap-3">
          {subject.students_behind > 0 && (
            <span className="flex items-center gap-1 text-red-400">
              <AlertTriangle className="w-3.5 h-3.5" />
              {subject.students_behind} behind
            </span>
          )}
          {subject.students_ahead > 0 && (
            <span className="flex items-center gap-1 text-emerald-400">
              <TrendingUp className="w-3.5 h-3.5" />
              {subject.students_ahead} ahead
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

interface StudentRowProps {
  student: StudentProgressForSubject;
  onSelect: () => void;
  isDark: boolean;
}

const StudentRow: React.FC<StudentRowProps> = ({ student, onSelect, isDark }) => {
  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-4 p-3 rounded-xl transition-colors group ${
        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
      }`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
        isDark ? 'bg-slate-700' : 'bg-slate-200'
      }`}>
        <span className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          {student.first_name.charAt(0)}{student.last_name.charAt(0)}
        </span>
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {student.first_name} {student.last_name}
          </span>
          <StatusBadge status={student.status} size="sm" />
        </div>
        <div className="mt-1">
          <ProgressBar percent={student.completion_percent} height="sm" />
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className={`text-right text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          <div className="font-medium">{formatPercent(student.completion_percent)}</div>
          <div className="text-xs">{student.topics_completed}/{student.total_topics} topics</div>
        </div>
        <Eye className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${
          isDark ? 'text-slate-400' : 'text-slate-500'
        }`} />
      </div>
    </button>
  );
};

// ============= MAIN COMPONENT =============

interface AdminProgressOverviewProps {
  onClose?: () => void;
}

type ViewMode = 'overview' | 'subject' | 'student' | 'behind';

export const AdminProgressOverview: React.FC<AdminProgressOverviewProps> = ({ onClose }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === 'dark';
  
  const { data: overview, isLoading: isLoadingOverview } = useAdminProgressOverview();
  const { data: subjectStudents, isLoading: isLoadingSubject } = useSubjectStudentsProgress(selectedSubjectId);
  const { data: studentDetail, isLoading: isLoadingStudent } = useStudentProgressDetail(selectedStudentId);
  const { data: studentsBehind, isLoading: isLoadingBehind } = useStudentsBehind(50);
  
  const handleSubjectSelect = (subjectId: string) => {
    setSelectedSubjectId(subjectId);
    setViewMode('subject');
  };
  
  const handleStudentSelect = (studentId: string) => {
    setSelectedStudentId(studentId);
    setViewMode('student');
  };
  
  const handleBack = () => {
    if (viewMode === 'student' && selectedSubjectId) {
      setSelectedStudentId(null);
      setViewMode('subject');
    } else {
      setSelectedSubjectId(null);
      setSelectedStudentId(null);
      setViewMode('overview');
    }
  };
  
  // Loading state
  if (isLoadingOverview && viewMode === 'overview') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading progress data...</p>
      </div>
    );
  }
  
  // Subject Detail View
  if (viewMode === 'subject' && subjectStudents) {
    return (
      <SubjectStudentsView
        data={subjectStudents}
        onBack={handleBack}
        onSelectStudent={handleStudentSelect}
        isDark={isDark}
        isLoading={isLoadingSubject}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    );
  }
  
  // Student Detail View
  if (viewMode === 'student' && studentDetail) {
    return (
      <StudentDetailView
        data={studentDetail}
        onBack={handleBack}
        isDark={isDark}
        isLoading={isLoadingStudent}
      />
    );
  }
  
  // Students Behind View
  if (viewMode === 'behind') {
    return (
      <StudentsBehindView
        students={studentsBehind || []}
        onBack={() => setViewMode('overview')}
        onSelectStudent={handleStudentSelect}
        isDark={isDark}
        isLoading={isLoadingBehind}
      />
    );
  }
  
  if (!overview) return null;
  
  // Overview View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Progress Analytics
          </h2>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Monitor student progress across all subjects
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              isDark 
                ? 'bg-white/10 text-white hover:bg-white/20' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Close
          </button>
        )}
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />}
          label="Total Students"
          value={overview.total_students}
          isDark={isDark}
          color="cyan"
        />
        <StatCard
          icon={<BookOpen className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />}
          label="Active Subjects"
          value={overview.total_subjects}
          isDark={isDark}
          color="purple"
        />
        <StatCard
          icon={<BarChart3 className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />}
          label="Avg. Completion"
          value={`${formatPercent(overview.average_completion)}`}
          isDark={isDark}
          color="green"
        />
        <StatCard
          icon={<AlertTriangle className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />}
          label="Behind Schedule"
          value={overview.students_behind}
          isDark={isDark}
          color="red"
        />
      </div>
      
      {/* Alert for students behind */}
      {overview.students_behind > 0 && (
        <button
          onClick={() => setViewMode('behind')}
          className={`w-full p-4 rounded-xl border flex items-center justify-between transition-colors ${
            isDark 
              ? 'bg-red-500/10 border-red-500/20 hover:bg-red-500/20' 
              : 'bg-red-50 border-red-200 hover:bg-red-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <div className="text-left">
              <p className={`font-medium ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                {overview.students_behind} students are behind schedule
              </p>
              <p className={`text-sm ${isDark ? 'text-red-400/80' : 'text-red-600'}`}>
                Click to view and take action
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-red-400" />
        </button>
      )}
      
      {/* Subject Cards */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Subjects Overview
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {overview.subject_summaries.map((subject) => (
            <SubjectCard
              key={subject.subject_id}
              subject={subject}
              onSelect={() => handleSubjectSelect(subject.subject_id)}
              isDark={isDark}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============= SUB-VIEWS =============

interface SubjectStudentsViewProps {
  data: {
    subject_id: string;
    subject_name: string;
    grade: string | null;
    total_students: number;
    average_completion: number;
    students_behind: number;
    students_on_track: number;
    students_ahead: number;
    students: StudentProgressForSubject[];
  };
  onBack: () => void;
  onSelectStudent: (studentId: string) => void;
  isDark: boolean;
  isLoading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const SubjectStudentsView: React.FC<SubjectStudentsViewProps> = ({
  data, onBack, onSelectStudent, isDark, isLoading, searchQuery, setSearchQuery
}) => {
  const filteredStudents = data.students.filter(s => 
    `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );
  
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading students...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
        >
          <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
        </button>
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {data.subject_name}
          </h2>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            {data.total_students} students • Avg: {formatPercent(data.average_completion)}
          </p>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`p-4 rounded-xl text-center ${
          isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'
        }`}>
          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {data.students_on_track + data.students_ahead}
          </span>
          <p className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>On Track</p>
        </div>
        <div className={`p-4 rounded-xl text-center ${
          isDark ? 'bg-red-500/10' : 'bg-red-50'
        }`}>
          <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {data.students_behind}
          </span>
          <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>Behind</p>
        </div>
        <div className={`p-4 rounded-xl text-center ${
          isDark ? 'bg-blue-500/10' : 'bg-blue-50'
        }`}>
          <TrendingUp className="w-6 h-6 text-blue-400 mx-auto mb-2" />
          <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {data.students_ahead}
          </span>
          <p className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Ahead</p>
        </div>
      </div>
      
      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
          isDark ? 'text-slate-500' : 'text-slate-400'
        }`} />
        <input
          type="text"
          placeholder="Search students..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm transition-colors focus:outline-none ${
            isDark 
              ? 'bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-cyan-500/50' 
              : 'bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-400'
          }`}
        />
      </div>
      
      {/* Students List */}
      <div className={`rounded-xl border divide-y ${
        isDark ? 'bg-white/5 border-white/10 divide-white/10' : 'bg-white border-slate-200 divide-slate-100'
      }`}>
        {filteredStudents.length === 0 ? (
          <div className="p-8 text-center">
            <Users className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              {searchQuery ? 'No students found' : 'No students enrolled'}
            </p>
          </div>
        ) : (
          filteredStudents.map((student) => (
            <StudentRow
              key={student.user_id}
              student={student}
              onSelect={() => onSelectStudent(student.user_id)}
              isDark={isDark}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface StudentDetailViewProps {
  data: ProgressOverview;
  onBack: () => void;
  isDark: boolean;
  isLoading: boolean;
}

const StudentDetailView: React.FC<StudentDetailViewProps> = ({ data, onBack, isDark, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading student details...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
        >
          <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
        </button>
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Student Progress
          </h2>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            {data.total_subjects} subjects • {formatPercent(data.overall_completion_percent)} overall
          </p>
        </div>
      </div>
      
      {/* Overall Progress Card */}
      <div className={`p-6 rounded-2xl border ${
        isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {formatPercent(data.overall_completion_percent)}
            </span>
            <StatusBadge status={data.overall_status} />
          </div>
        </div>
        <ProgressBar percent={data.overall_completion_percent} />
        <p className={`mt-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          {data.status_message}
        </p>
      </div>
      
      {/* Subject List */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Subject Progress
        </h3>
        <div className="space-y-3">
          {data.subjects.map((subject) => {
            return (
              <div
                key={subject.subject_id}
                className={`p-4 rounded-xl border ${
                  isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    {subject.subject_name}
                  </span>
                  <StatusBadge status={subject.status} size="sm" />
                </div>
                <ProgressBar percent={subject.completion_percent} height="sm" />
                <div className={`flex items-center justify-between mt-2 text-sm ${
                  isDark ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span>{subject.topics_completed}/{subject.total_topics} topics</span>
                  <span>{formatPercent(subject.completion_percent)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface StudentsBehindViewProps {
  students: StudentProgressSummary[];
  onBack: () => void;
  onSelectStudent: (studentId: string) => void;
  isDark: boolean;
  isLoading: boolean;
}

const StudentsBehindView: React.FC<StudentsBehindViewProps> = ({
  students, onBack, onSelectStudent, isDark, isLoading
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading students...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
        >
          <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
        </button>
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Students Behind Schedule
          </h2>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            {students.length} students need attention
          </p>
        </div>
      </div>
      
      {/* Students List */}
      <div className={`rounded-xl border divide-y ${
        isDark ? 'bg-white/5 border-white/10 divide-white/10' : 'bg-white border-slate-200 divide-slate-100'
      }`}>
        {students.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle2 className={`w-12 h-12 mx-auto mb-3 text-emerald-400`} />
            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
              All students are on track!
            </p>
            <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              No students are currently behind schedule.
            </p>
          </div>
        ) : (
          students.map((student) => (
            <button
              key={student.user_id}
              onClick={() => onSelectStudent(student.user_id)}
              className={`w-full flex items-center gap-4 p-4 transition-colors ${
                isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-red-500/20' : 'bg-red-100'
              }`}>
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1 text-left">
                <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {student.first_name} {student.last_name}
                </span>
                <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {formatPercent(student.overall_completion_percent)} complete • {student.subjects_count} subjects
                </div>
              </div>
              <StatusBadge status={student.overall_status} size="sm" />
              <ChevronRight className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminProgressOverview;

